import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINES = [
  'Architect',
  'Structural Engineer',
  'MEP Engineer',
  'Civil Engineer',
  'Project Manager',
  'General Contractor',
  'Electrical Contractor',
  'Mechanical Contractor',
  'Plumbing Contractor',
  'Owner Representative',
  'Construction Manager',
  'Interior Designer',
  'Landscape Architect',
  'Surveyor',
  'Geotechnical Engineer',
  'Environmental Consultant',
  'Other'
];

export const ProjectResourcesTab = ({ projectId }) => {
  const [resources, setResources] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    discipline: '',
    role: '',
    notes: ''
  });

  useEffect(() => {
    fetchResources();
  }, [projectId]);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/resources`);
      setResources(response.data);
    } catch (error) {
      toast.error('Failed to load resources');
    }
  };

  const createOrUpdateResource = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.patch(`${API}/resources/${editingId}`, formData);
        toast.success('Resource updated!');
      } else {
        await axios.post(`${API}/projects/${projectId}/resources`, formData);
        toast.success('Resource added!');
      }
      setShowCreateModal(false);
      setEditingId(null);
      resetForm();
      fetchResources();
    } catch (error) {
      toast.error(editingId ? 'Failed to update resource' : 'Failed to add resource');
    }
  };

  const editResource = (resource) => {
    setFormData({
      name: resource.name,
      organization: resource.organization,
      email: resource.email,
      phone: resource.phone || '',
      discipline: resource.discipline,
      role: resource.role || '',
      notes: resource.notes || ''
    });
    setEditingId(resource.id);
    setShowCreateModal(true);
  };

  const deleteResource = async (resourceId) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      await axios.delete(`${API}/resources/${resourceId}`);
      toast.success('Resource deleted');
      fetchResources();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      organization: '',
      email: '',
      phone: '',
      discipline: '',
      role: '',
      notes: ''
    });
  };

  // Group resources by discipline
  const groupedResources = resources.reduce((acc, resource) => {
    const discipline = resource.discipline || 'Other';
    if (!acc[discipline]) acc[discipline] = [];
    acc[discipline].push(resource);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="resources-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Project Resources</h2>
          <p className="text-sm text-slate-600 mt-1">Team members and stakeholders</p>
        </div>
        <button
          onClick={() => { setEditingId(null); resetForm(); setShowCreateModal(true); }}
          className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800"
          data-testid="add-resource-button"
        >
          Add Resource
        </button>
      </div>

      {/* Resources Grid */}
      <div className="space-y-6">
        {Object.entries(groupedResources).map(([discipline, disciplineResources]) => (
          <div key={discipline}>
            <h3 className="text-lg font-semibold text-slate-800 mb-3 border-b pb-2">{discipline}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {disciplineResources.map((resource) => (
                <div
                  key={resource.id}
                  className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow"
                  data-testid={`resource-card-${resource.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold text-slate-900">{resource.name}</h4>
                      <p className="text-sm text-slate-600">{resource.organization}</p>
                    </div>
                    {resource.role && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                        {resource.role}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <a href={`mailto:${resource.email}`} className="text-blue-600 hover:underline">
                        {resource.email}
                      </a>
                    </div>
                    {resource.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <a href={`tel:${resource.phone}`} className="hover:underline">
                          {resource.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {resource.notes && (
                    <p className="mt-3 text-xs text-slate-500 italic">{resource.notes}</p>
                  )}

                  <div className="mt-4 pt-3 border-t flex gap-2">
                    <button
                      onClick={() => editResource(resource)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteResource(resource.id)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {resources.length === 0 && (
          <div className="text-center py-12 bg-slate-50 rounded-lg">
            <p className="text-slate-600">No resources added yet</p>
            <p className="text-sm text-slate-500 mt-1">Add team members and stakeholders to this project</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="resource-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {editingId ? 'Edit Resource' : 'Add Resource'}
            </h2>
            <form onSubmit={createOrUpdateResource} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    placeholder="John Smith"
                    data-testid="resource-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Organization *</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({...formData, organization: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    placeholder="ABC Engineering"
                    data-testid="resource-org-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    placeholder="john@example.com"
                    data-testid="resource-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    placeholder="+1 (555) 123-4567"
                    data-testid="resource-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Discipline *</label>
                  <select
                    value={formData.discipline}
                    onChange={(e) => setFormData({...formData, discipline: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    required
                    data-testid="resource-discipline-select"
                  >
                    <option value="">Select discipline</option>
                    {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    placeholder="Lead Designer"
                    data-testid="resource-role-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    rows="2"
                    placeholder="Additional notes..."
                    data-testid="resource-notes-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800"
                  data-testid="submit-resource-button"
                >
                  {editingId ? 'Update' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setEditingId(null); }}
                  className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
